import { generateId } from "@fleet/utils";
import { liveWorkspaceFilters, liveWorkspaceMemberFilters, queryKeys } from "@time-tracker/shared";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  type JSX,
  untrack,
  useContext,
} from "solid-js";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { and, eq, workspace, type Workspace, workspace_member } from "@time-tracker/powersync";
import { showToast } from "@/components/ui/toast";
import { slugify } from "@/features/chronos-sync/helpers";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

const ACTIVE_WORKSPACE_STORAGE_KEY = "chronos.active-workspace";

interface WorkspaceContextValue {
  /** Workspaces where the user is an active member. */
  workspaces: () => Workspace[];
  activeWorkspaceId: () => string;
  activeWorkspace: () => Workspace | null;
  setActiveWorkspace: (id: string) => void;
  createWorkspace: (name: string) => Promise<string | null>;
}

const WorkspaceContext = createContext<WorkspaceContextValue>();

function readStoredWorkspaceId(): string {
  try {
    return globalThis.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function storeWorkspaceId(id: string) {
  try {
    globalThis.localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, id);
  } catch {
    // localStorage can fail (private mode); in-memory state is sufficient.
  }
}

export function WorkspaceProvider(props: { children: JSX.Element }) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  // Deep-link: active workspace travels in `?ws=`. On mount we pick the one from
  // the URL if present (pasted/shared link takes precedence); otherwise, stored
  // value from localStorage. Then we mirror active back to the URL so the link
  // is copyable and renders the same screen in another browser. `strict: false`
  // because provider is not a route component and lives under multiple child routes.
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const urlWorkspaceId = (): string => {
    const value = (search() as { ws?: unknown }).ws;
    return typeof value === "string" && value.trim() !== "" ? value : "";
  };

  const [selectedId, setSelectedId] = createSignal<string>(
    urlWorkspaceId() || readStoredWorkspaceId(),
  );

  const workspacesQuery = createPowerSyncQuery<Workspace>({
    queryKey: () => queryKeys.chronos.myWorkspaces(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select({
          id: workspace.id,
          name: workspace.name,
          slug: workspace.slug,
          created_at: workspace.created_at,
          updated_at: workspace.updated_at,
          deleted_at: workspace.deleted_at,
        })
        .from(workspace)
        .innerJoin(workspace_member, eq(workspace_member.workspace_id, workspace.id))
        .where(
          and(
            eq(workspace_member.user_id, userId()),
            ...liveWorkspaceMemberFilters(),
            ...liveWorkspaceFilters(),
          ),
        );
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  const workspaces = createMemo(() => workspacesQuery.data ?? []);

  // Active workspace must always be one of mine; if stored one no longer
  // exists (removed, deleted) fall back to first available.
  const activeWorkspaceId = createMemo(() => {
    const list = workspaces();
    const selected = selectedId();
    if (selected && list.some((w) => w.id === selected)) {
      return selected;
    }
    return list[0]?.id ?? "";
  });

  const activeWorkspace = createMemo(
    () => workspaces().find((w) => w.id === activeWorkspaceId()) ?? null,
  );

  createEffect(() => {
    const id = activeWorkspaceId();
    if (id) {
      storeWorkspaceId(id);
    }
  });

  // URL → signal: back/forward or pasted link with `?ws=` switches workspace.
  // Idempotent on reload: URL reconstructs the same active workspace.
  // Comparison runs inside `untrack`: the effect must ONLY react to EXTERNAL
  // changes of `?ws=`. If it tracked `selectedId()` (the signal it writes), a
  // state-first switch would re-trigger the effect with the STILL old URL (navigate is
  // async) and REVERT the newly chosen workspace — since the signal→URL effect
  // below reads `activeWorkspaceId()` (already reverted), `?ws=` never changes and the
  // switch is undone deterministically (bug #15b / CC-16).
  createEffect(() => {
    const fromUrl = urlWorkspaceId();
    if (fromUrl && untrack(() => fromUrl !== selectedId())) {
      setSelectedId(fromUrl);
    }
  });

  // Signal → URL: once active workspace is resolved we reflect it in `?ws=`
  // (replace, to avoid flooding history). Only write if different to
  // avoid redundant navigations. Preserve other search params.
  createEffect(() => {
    const id = activeWorkspaceId();
    if (!id || id === urlWorkspaceId()) {
      return;
    }
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, ws: id }),
      replace: true,
    });
  });

  const setActiveWorkspace = (id: string) => {
    setSelectedId(id);
    storeWorkspaceId(id);
  };

  const createWorkspace = async (name: string): Promise<string | null> => {
    const dbInstance = drizzle();
    const currentUserId = userId();
    const trimmed = name.trim();
    if (!dbInstance || !currentUserId || !trimmed) {
      return null;
    }
    try {
      const workspaceId = generateId();
      const slug = slugify(trimmed);
      await dbInstance.insert(workspace).values({
        id: workspaceId,
        name: trimmed,
        slug: slug || workspaceId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      await dbInstance.insert(workspace_member).values({
        id: generateId(),
        workspace_id: workspaceId,
        user_id: currentUserId,
        role: "owner",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setActiveWorkspace(workspaceId);
      showToast(t(msg`Workspace "${trimmed}" created`), "success");
      return workspaceId;
    } catch (err) {
      console.error("Failed to create workspace", err);
      showToast(t(msg`Could not create the workspace`), "error");
      return null;
    }
  };

  const value: WorkspaceContextValue = {
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    setActiveWorkspace,
    createWorkspace,
  };

  return <WorkspaceContext.Provider value={value}>{props.children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
}
